import { ref } from 'vue'

import { api } from '@/core/api/endpoints'

/**
 * LINE LIFF 연동 (Design §9).
 * 원본 App.js의 초기화 + OrderStore의 accessToken 취득을 옮겼다.
 *
 * SDK는 LINE 인앱에서만 필요하므로 동적 import로 지연 로드한다.
 */

type LiffSdk = (typeof import('@line/liff'))['default']

let sdk: LiffSdk | null = null
const isReady = ref(false)
const isInClient = ref(false)

async function loadSdk(): Promise<LiffSdk> {
  if (sdk) return sdk
  const mod = await import('@line/liff')
  sdk = mod.default
  return sdk
}

/**
 * 경로에서 매장을 해석한다.
 *
 * LIFF는 엔드포인트 리다이렉트 전에 원래 경로를 `?liff.state=`에 담아 온다.
 * 그때는 현재 pathname이 아니라 그 값을 봐야 한다 (원본 동일).
 */
export function resolveTenantFromLocation(
  pathname: string,
  search: string,
): { companyId: string; storeCode: string } | null {
  const liffState = new URLSearchParams(search).get('liff.state')
  // liff.state 안에 다시 쿼리가 붙어 있을 수 있어 '?' 앞까지만 쓴다
  const target = liffState ? liffState.split('?')[0]! : pathname

  const segments = target.split('/').filter(Boolean)
  if (segments.length < 2) return null

  return { companyId: segments[0]!.toLowerCase(), storeCode: segments[1]! }
}

export function useLiff() {
  /**
   * LINE 인앱이면 매장의 LIFF ID를 받아 초기화한다.
   * 인앱이 아니면 아무것도 하지 않는다 (일반 브라우저 접속).
   */
  async function init(): Promise<void> {
    try {
      const liff = await loadSdk()
      isInClient.value = liff.isInClient()
      if (!isInClient.value) return

      const tenant = resolveTenantFromLocation(window.location.pathname, window.location.search)
      if (!tenant) return

      const { data: liffId } = await api.getLiffId(tenant.companyId, tenant.storeCode)
      if (!liffId) return

      // ★원본은 await하지 않아 초기화 완료 전에 다음 코드가 실행됐다 (Design §6.4 #3)
      await liff.init({ liffId })
      isReady.value = true
    } catch {
      // LIFF 실패가 일반 주문 흐름을 막아서는 안 된다
      isReady.value = false
    }
  }

  /** 주문 생성 시 서버로 보낼 액세스 토큰. 인앱이 아니면 빈 문자열 */
  function getAccessToken(): string {
    if (!sdk || !isInClient.value || !isReady.value) return ''
    return sdk.getAccessToken() ?? ''
  }

  /** SoftBank 결제가 쓰는 LINE 프로필 */
  async function getProfile() {
    if (!sdk || !isInClient.value || !isReady.value) return null
    try {
      return await sdk.getProfile()
    } catch {
      return null
    }
  }

  return { isReady, isInClient, init, getAccessToken, getProfile }
}
